import useFetchDataWithOrg from 'hooks/useFetchDataWithOrg';
import { useEffect, useMemo, useRef, useState } from 'react';
import useZoomByMouseWheel from '../Hooks/useZoomByMouseWheel';
import { createForegroundPositionExpression, createHandleFgImgMouseMove } from '../IGconfig';
import CompetitionLogo from '../Parts/CompetitionLogo';
import IGHeader from '../Parts/IGHeader';
import IGImg from '../Parts/IGImg';
import TableStandings from '../Parts/TablePM';

const IG5_StandingsByPoints_Core = ({ 
  exportRef,
  metadata,
  size,
  fgImgPositionX,
  fgImgPositionY,
  setFgImgPositionX,
  setFgImgPositionY,
  fgImgZoom,
  setFgImgZoom,
  fgPictureUrl,
  fgImgMargin,
  backgroundMode,
  backgroundImgUrl,
  backgroundColor,
  phaseIdsAll,
  enablePositioning = true,
  useImageProxy = true,
  isRolling = false,
  rollingRowsPerPage = 10,
  rollingIntervalSeconds = 5
}) => {
  const fgImgRef = useRef(null);
  const effectiveMode = backgroundMode ?? (backgroundImgUrl ? 'IMAGE' : backgroundColor ? 'COLOR' : 'DEFAULT');
  
  // Always call hooks, but conditionally use positioning functionality
  useZoomByMouseWheel(fgImgRef, enablePositioning ? fgImgZoom : null, enablePositioning ? setFgImgZoom : () => {});
  const handleFgImgMouseMove = enablePositioning 
    ? createHandleFgImgMouseMove(setFgImgPositionX, setFgImgPositionY)
    : null;

  // Load standings data using public API
  const teamStandings = useFetchDataWithOrg(phaseIdsAll != null
    ? `/public/standings?phaseIds=${phaseIdsAll.length > 0 ? phaseIdsAll.join(',') : ''}`
    : null);

  const allData = useMemo(() => {
    const data = teamStandings?.data?.map((team) => ({
      Name: team["Team Name"],
      Logo: team["TeamLogo"]?.replace("[size]", "cropped_md"),
      Z: team["Total Games"],
      V: team["Wins"],
      P: team["Losses"],
      R: team["Draws"],
      Score: team["Score"],
      B: team["Total Points"],
      TM: team["Total Penalty Minutes"],
    })) ?? [];

    const sorted = data.sort((a, b) => {
      return b.B - a.B;
    });

    return sorted;
  }, [teamStandings]);

  // Rolling display logic
  const [currentPage, setCurrentPage] = useState(0);
  const recordsPerPage = Math.max(1, Math.min(50, rollingRowsPerPage ?? 10));

  const data = useMemo(() => {
    if (!isRolling || allData.length <= recordsPerPage) {
      return allData;
    }

    const startIndex = currentPage * recordsPerPage;
    const endIndex = startIndex + recordsPerPage;
    const slicedData = allData.slice(startIndex, endIndex);
    
    // Add original position information to each team for proper numbering
    return slicedData.map((team, index) => ({
      ...team,
      originalIndex: startIndex + index
    }));
  }, [allData, isRolling, currentPage]);

  // Rolling timer effect
  useEffect(() => {
    if (!isRolling || allData.length <= recordsPerPage) {
      return;
    }

    const totalPages = Math.ceil(allData.length / recordsPerPage);
    const intervalMs = Math.max(1000, Math.min(120000, (rollingIntervalSeconds ?? 5) * 1000));
    const interval = setInterval(() => {
      setCurrentPage((prevPage) => (prevPage + 1) % totalPages);
    }, intervalMs);

    return () => clearInterval(interval);
  }, [isRolling, allData.length, recordsPerPage, rollingIntervalSeconds]);

  if (phaseIdsAll == null) {
    return null;
  }

  return (
    <article
      ref={exportRef}
      className="mx-auto w-[625px] bg-hockey-page-bg font-header text-white"
      style={{
        width: `${size?.width}px`,
        height: `${size?.height}px`,
        paddingTop: `${fgImgMargin.top}px`,
        paddingLeft: `${fgImgMargin.left}px`,
        paddingRight: `${fgImgMargin.right}px`,
        paddingBottom: `${fgImgMargin.bottom}px`,
        fontSize: '14px',
        boxSizing: 'border-box',
        ...(effectiveMode === 'COLOR' && backgroundColor
          ? { backgroundColor }
          : effectiveMode === 'IMAGE' && backgroundImgUrl
            ? {
                backgroundImage: `url('${useImageProxy ? `${process.env.REACT_APP_IMAGE_PROXY_URL}?url=${backgroundImgUrl}` : backgroundImgUrl}')`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
              }
            : {
                backgroundColor: '#323f50',
              }),
      }}
    >
      <div className="flex h-full flex-col items-center justify-between">

        <div className='p-8'>
          <IGHeader
            title={`Standings - ${metadata.CompetitionName} ${metadata.SeasonName}`}
            league={metadata.GroupName}
            leagueStyle={{ color: metadata.GroupColor ?? '#777777' }}
            small
            date={metadata.Date}
          />
          <TableStandings data={data} isRolling={isRolling} />
        </div>

        <div className='w-full'>
          <div className="relative h-[80px] z-10">
            <IGImg
              src="/images/most-points.png"
              className="absolute top-0 -mt-14 h-[250px]"
              alt=""
            />
          </div>
          <div
            ref={fgImgRef}
            onMouseMove={handleFgImgMouseMove}
            className='w-full z-20 relative aspect-[24/10]'
            style={{
              backgroundImage: `url('${useImageProxy ? `${process.env.REACT_APP_IMAGE_PROXY_URL}?url=${fgPictureUrl}` : fgPictureUrl}')`,
              backgroundSize: `${fgImgZoom ?? 100}%`,
              backgroundPosition: createForegroundPositionExpression(fgImgPositionX, fgImgPositionY),
              backgroundRepeat: 'no-repeat',
              cursor: enablePositioning ? 'grab' : 'default',
              clipPath: "polygon(450px 0, 100% 0, 100% 100%, 0 100%, 0 70px)",
            }}
            onMouseDown={enablePositioning ? (e) => e.target.style.cursor = 'grabbing' : undefined}
            onMouseUp={enablePositioning ? (e) => e.target.style.cursor = 'grab' : undefined}
          ></div>
          <CompetitionLogo
            className="mx-auto scale-75 p-4"
            src={metadata.CompetitionLogo}
            title={metadata.CompetitionName}
            season={metadata.SeasonName}
            useImageProxy={useImageProxy}
          />
        </div>
      </div>
    </article>
  );
};

export default IG5_StandingsByPoints_Core;
